<template>
  <form @submit.prevent>
    <div class="row">
      <div class="col-md-12"><p class="control-label">Organization</p></div>
    </div>
    <div class="row">
      <div class="col-md-6">
        <fg-input type="text"
          placeholder="Name"
          :class="{ 'form-group--error': $v.name.$error }"
          v-model="$v.name.$model">
        </fg-input>
      </div>
      <div class="col-md-6">
        <fg-input type="text"
          placeholder="CRM ID"
          :class="{ 'form-group--error': $v.crmId.$error }"
          v-model="$v.crmId.$model">
        </fg-input>
      </div>
    </div>
    <div class="row">
      <div class="col-md-6">
        <fg-input type="number"
          placeholder="Check-in Interval"
          :class="{ 'form-group--error': $v.refreshInterval.$error }"
          v-model="$v.refreshInterval.$model"
          addonRightText="days">
        </fg-input>
      </div>
      <div class="col-md-6">
        <select-country :validator="$v.selectedCountry" />
      </div>
    </div>
    <template v-if="isCreating">
      <div class="row">
        <div class="col-md-12"><p class="control-label">Root Group</p></div>
      </div>
      <div class="row">
        <div class="col-md-4">
          <fg-input type="text"
            placeholder="Name"
            :class="{ 'form-group--error': $v.rootName.$error }"
            v-model="$v.rootName.$model">
          </fg-input>
        </div>
        <div class="col-md-8">
          <fg-input type="text"
            placeholder="Description"
            :class="{ 'form-group--error': $v.rootDescription.$error }"
            v-model="$v.rootDescription.$model">
          </fg-input>
        </div>
      </div>
      <div class="row">
        <div class="col-md-12"><p class="control-label">Manager</p></div>
      </div>
      <div class="row">
        <div class="col-md-4">
          <fg-input type="text"
            placeholder="Full Name"
            :class="{ 'form-group--error': $v.userName.$error }"
            v-model="$v.userName.$model">
          </fg-input>
        </div>
        <div class="col-md-8">
          <fg-input type="email"
            placeholder="Email"
            :class="{ 'form-group--error': $v.userEmail.$error }"
            v-model="$v.userEmail.$model">
          </fg-input>
        </div>
      </div>
    </template>
    <div class="row">
      <div class="col-md-12"><p class="control-label">Contract</p></div>
    </div>
    <div class="row">
      <div class="col-md-6">
        <div class="form-group">
          <date-picker
            :input-props="{ placeholder: 'Start Date', class: 'form-control' }"
            :masks="{ L: 'D MMM YYYY' }"
            :max-date="maxDate"
            :class="{ 'form-group--error': $v.contractStart.$error }"
            v-model="$v.contractStart.$model"
            :popover="{ visibility: 'click' }" />
        </div>
      </div>
      <div class="col-md-6">
        <div class="form-group">
          <date-picker
            :input-props="{ placeholder: 'End Date', class: 'form-control' }"
            :masks="{ L: prettyDateFormat }"
            :min-date="minDate"
            :class="{ 'form-group--error': $v.contractEnd.$error }"
            v-model="$v.contractEnd.$model"
            :popover="{ visibility: 'click' }" />
        </div>
      </div>
    </div>
    <div class="row" v-if="!isCreating">
      <div class="col-12">
        <p v-if="isEulaAccepted" class="text-info">EULA accepted by {{ this.eulaAcceptedByName }}</p>
        <p v-else class="text-danger">EULA NOT accepted</p>
      </div>
    </div>
    <br>
    <div class="row">
      <div class="col-12">
        <p-button type="info"
          :loading="isSaving"
          @click.native.prevent="save">
          <span v-if="isCreating">Create</span>
          <span v-else>Save</span>
        </p-button>
      </div>
    </div>
    <div class="clearfix"></div>
  </form>
</template>

<script>
import SelectCountry from '../Inputs/SelectCountry.vue';
import { required, requiredIf, email, minValue } from 'vuelidate/lib/validators';
import { notify } from '../../services/notify';
import config from '../../constants/config';
import moment from 'moment';

export default {
  name: 'OrganizationForm',
  components: {
    SelectCountry
  },
  props: {
    isSaving: Boolean,
    organization: {
      type: Object,
      required: false
    }
  },
  data() {
    return {
      name: "",
      crmId: "",
      selectedCountry: "",
      rootName: "",
      rootDescription: "",
      userName: "",
      userEmail: "",
      contractStart: "",
      contractEnd: "",
      refreshInterval: null,
      isEulaAccepted: false,
      eulaAcceptedByName: ""
    }
  },
  validations: {
    name: { required },
    crmId: {},
    rootName: {},
    rootDescription: {},
    userName: { required: requiredIf(function () { return this.isCreating }) },
    userEmail: { required: requiredIf(function () { return this.isCreating }), email },
    contractStart: { required: requiredIf(function () { return this.organization && this.organization.contract_start }) },
    contractEnd: { required: requiredIf(function () { return this.organization && this.organization.contract_end }) },
    selectedCountry: { required },
    refreshInterval: { required, minValue: minValue(0) }
  },
  created() {
    if (this.organization) {
      this.name = this.organization.name;
      this.crmId = this.organization.crm_id;
      this.selectedCountry = this.organization.country;
      this.contractStart = this.getDate(this.organization.contract_start);
      this.contractEnd = this.getDate(this.organization.contract_end);
      this.refreshInterval = this.organization.refresh_interval;
      this.isEulaAccepted = this.organization.is_eula_accepted;
      this.eulaAcceptedByName = this.organization.eula_accepted_user.full_name;
    }
  },
  methods: {
    save() {
      this.$v.$touch();
      if (this.$v.$invalid) {
        let fields = this.isCreating ? "a name, country and manager" : "a name and country";
        notify().error(`Please provide ${fields} for the organization.`)
        return;
      }

      let organization = {
        name: this.name,
        crm_id: this.crmId,
        country_id: this.selectedCountry.id
      };

      if (this.contractStart) {
        organization.contract_start = moment(this.contractStart).toISOString();
      }

      if (this.contractEnd) {
        organization.contract_end = moment(this.contractEnd).toISOString();
      }

      if (this.refreshInterval) {
        organization.refresh_interval = this.refreshInterval;
      }

      if (this.isCreating) {
        const user = { full_name: this.userName, email: this.userEmail };
        organization.user = user;

        if (this.rootName) {
          const rootGroup = { name: this.rootName, description: this.rootDescription };
          organization.root_group = rootGroup;
        }
      } else {
        organization.id = this.organization.id;
      }

      this.$emit('saveOrganization', organization);
    },
    updateCountry(country) {
      this.selectedCountry = country;
    },
    getDate(dateString) {
      if (!dateString) {
        return null;
      }
      return moment(dateString).toDate();
    }
  },
  computed: {
    maxDate() {
      return this.getDate(this.contractEnd);
    },
    minDate() {
      return this.getDate(this.contractStart);
    },
    isCreating() {
      return !this.organization;
    },
    prettyDateFormat() {
      return config.PRETTY_DATE_FORMAT;
    }
  }
};
</script>
